import type { LocationQueryValue, RouteParams } from 'vue-router'
import apiClient from '@shared/api'
import type IPatient from '@shared/api/patients/resources/patients/patient'
import type IPatientResult from '@shared/api/patients/resources/patients/patient-result'
import type IPatientParams from '@shared/api/patients/resources/patients/patient-params'
import type IPatientSummaryParams from '@shared/api/patients/resources/patients/patient-summary-params'
import type IContact from '@shared/api/patients/resources/contacts/contact'
import type INewContactPayload from '@shared/api/patients/resources/contacts/new-contact-payload'
import type IOrganizationPatientSummary from '@shared/api/patients/resources/summary/organization-patient-summary'
import type ICommunitySummary from '@shared/api/patients/resources/summary/community-summary'
import type IPatientsSummary from '@shared/api/patients/resources/summary/patients-summary'
import type IPatientCommunity from '@shared/api/patients/resources/patients/patient-community'
import type IPatientIdentity from '@shared/api/patients/resources/patients/patient-identity'
import type IPatientDemographics from '@shared/api/patients/resources/patients/patient-demographics'
import type ISocialSecurityNumber from '@shared/api/patients/resources/patients/social-security-number'
import type IPatientEnrollments from '@shared/api/patients/resources/patients/patient-enrollments'
import type IEditPatientEnrollments from '@shared/types/constants/edit-patient-enrollments'
import type IPatientPortalAccess from '@shared/api/patients/resources/patients/patient-portal-access'
import type IPatientListParams from '@shared/api/patients/resources/patients/patient-list-params'
import type ISendInvite from '@shared/types/contacts/send-invite'
import type IPatientPortalToken from '@shared/api/patients/resources/patients/patient-portal-token'
import type IPatientPortalRegistration from '@shared/api/patients/resources/patients/patient-portal-registration'
import type ICreatePortalUser from '@shared/api/patients/resources/patients/create-portal-user'
import type IPatientPortalUserInfo from '@shared/api/patients/resources/patients/patient-portal-user-info'
import { joinCommunityIds, type LocationQuery } from '@shared/utils/helpers'

export const searchPatient = ({
  name,
  dob,
  organizationId,
  communityId,
  includeInactive,
}: {
  name?: string | LocationQueryValue[]
  organizationId?: string | string[]
  dob?: string
  communityId?: string
  includeInactive?: string
}): Promise<IPatient[]> => {
  return apiClient.get('/patients/search', {
    params: { name, organizationId, communityId, includeInactive, dob },
  })
}

export const getContactRelationshipList = (): Promise<string[]> => {
  return apiClient.get('/patients/contact-relationships')
}

export const getPatient = ({
  patientId,
  params,
}: {
  patientId: string | string[]
  params?: IPatientParams
}): Promise<IPatient> => {
  return apiClient.get(`/patients/${patientId}`, { params })
}

export const getPatientContactList = ({
  patientId,
}: {
  patientId: string | string[] | RouteParams
}): Promise<IContact[]> => {
  return apiClient.get(`/patients/${patientId}/contacts`)
}

export const createPatientContact = ({
  patientId,
  data,
}: {
  patientId: string | string[] | RouteParams
  data: INewContactPayload
}): Promise<IContact> => {
  return apiClient.post(`/patients/${patientId}/contacts`, data)
}

export const updatePatientContact = ({
  patientId,
  contactId,
  data,
}: {
  patientId: string | string[] | RouteParams
  contactId: string | string[] | RouteParams
  data: INewContactPayload
}): Promise<IContact> => {
  return apiClient.put(`/patients/${patientId}/contacts/${contactId}`, data)
}

export const getPatientContact = ({
  patientId,
  contactId,
}: {
  patientId: string | string[] | RouteParams
  contactId: string | string[] | RouteParams
}): Promise<IContact> => {
  return apiClient.get(`/patients/${patientId}/contacts/${contactId}`)
}

export const deletePatientContact = ({
  patientId,
  contactId,
}: {
  patientId: string | string[] | RouteParams
  contactId: string | string[] | RouteParams
}): Promise<number> => {
  return apiClient.delete(`/patients/${patientId}/contacts/${contactId}`)
}

export const getPatientsList = (params: IPatientListParams): Promise<IPatientResult> => {
  return apiClient.get(`/patients?`, { params })
}

export const createPatient = ({
  communityId,
  beginDate,
  patientData,
}: {
  communityId: string | string[]
  beginDate: string
  patientData: IPatientIdentity
}): Promise<IPatient> => {
  return apiClient.post(`/patients?communityId=${communityId}&beginDate=${beginDate}`, patientData)
}

export const getPatientDemographics = ({ patientId }: { patientId: string }): Promise<IPatientDemographics> => {
  return apiClient.get(`/patients/${patientId}/demographics`)
}

export const updatePatientDemographics = ({
  patientId,
  payload,
}: {
  patientId: string | string[] | RouteParams
  payload: IPatientDemographics
}): Promise<IPatientDemographics> => {
  return apiClient.put(`/patients/${patientId}/demographics`, payload)
}

export const patientSummaryByOrganization = ({
  organizationId,
  communityIds,
}: LocationQuery): Promise<IOrganizationPatientSummary> => {
  return apiClient.get(`/patients/summary/organizations/${organizationId}${joinCommunityIds(communityIds)}`)
}

export const getPatientsReport = ({
  organizationId,
  communityIds,
}: {
  organizationId: string
  communityIds: string[]
}): Promise<string> => {
  return apiClient.get(`/patients/consent-report/organizations/${organizationId}${joinCommunityIds(communityIds)}`)
}

export const getPatientsByCommunityId = ({ communityId }: { communityId: string | string[] }): Promise<IPatient[]> => {
  return apiClient.get(`/patients?communityId=${communityId}`)
}

export const getPatientsSummaryByCommunity = ({
  communityId,
}: {
  communityId: string | string[]
}): Promise<ICommunitySummary> => {
  return apiClient.get(`/patients/summary/communities/${communityId}`)
}

export const getPatientSSN = ({ patientId }: { patientId: string | string[] }): Promise<ISocialSecurityNumber> => {
  return apiClient.get(`/patients/${patientId}/social-security-number`)
}

export const resyncPatient = ({ patientId }: { patientId: string | string[] }): Promise<number> => {
  return apiClient.post(`/patients/${patientId}/resync`)
}

export const deletePatientFromCommunity = ({
  patientId,
  communityId,
}: {
  patientId: string | string[]
  communityId: string | string[]
}): Promise<number> => {
  return apiClient.delete(`/patients/${patientId}/communities/${communityId}`)
}

export const movePatientToCommunity = ({
  patientId,
  communityId,
  payload,
}: {
  patientId: string | string[]
  communityId: string | string[]
  payload: { startDate: string }
}): Promise<IPatientCommunity> => {
  return apiClient.put(`/patients/${patientId}/communities/${communityId}`, payload)
}

export const updatePatientIdentities = ({
  patientId,
  payload,
}: {
  patientId: string | string[] | RouteParams
  payload: IPatientIdentity
}): Promise<IPatientIdentity> => {
  return apiClient.put(`/patients/${patientId}/identities`, payload)
}

export const updatePatientSSN = ({
  patientId,
  payload,
}: {
  patientId: string | string[] | RouteParams
  payload: ISocialSecurityNumber
}): Promise<ISocialSecurityNumber> => {
  return apiClient.put(`/patients/${patientId}/social-security-number`, payload)
}

export const updateProgramList = ({
  patientId,
  data,
}: {
  patientId: string | string[] | RouteParams
  data: IEditPatientEnrollments
}): Promise<IPatientEnrollments> => {
  return apiClient.put(`/patients/${patientId}/enrollments`, data)
}

export const getPortalRegistration = (): Promise<IPatientPortalRegistration[]> => {
  return apiClient.get(`/patients/portal-registrations`)
}

export const getPortalRegistrationToken = ({
  token,
}: {
  token: string | string[] | LocationQueryValue[]
}): Promise<IPatientPortalToken> => {
  const tokenQuery = token ? `?token=${token}` : ''
  return apiClient.get(`/patients/portal-registrations/tokens${tokenQuery}`)
}

export const createUserForPatientPortal = ({
  token,
  payload,
}: {
  token: string | LocationQueryValue | LocationQueryValue[]
  payload: ICreatePortalUser
}): Promise<IPatientPortalRegistration> => {
  const tokenQuery = token ? `?token=${token}` : ''
  return apiClient.post(`/patients/portal-registrations/users${tokenQuery}`, payload)
}

export const getPortalPatients = ({
  patientId,
}: {
  patientId: string | string[] | RouteParams
}): Promise<IPatientPortalAccess[]> => {
  return apiClient.get(`/patients/${patientId}/portal-registrations`)
}

export const sendInviteToPatientPortal = ({
  patientId,
  payload,
}: {
  patientId: string | string[] | RouteParams
  payload: ISendInvite
}): Promise<IPatientPortalAccess> => {
  return apiClient.post(`/patients/${patientId}/portal-registrations`, payload)
}

export const blockAccessToPatientPortal = ({
  patientId,
  userId,
  disabled,
}: {
  patientId: string | string[] | RouteParams
  userId: string | string[] | RouteParams
  disabled: boolean
}): Promise<IPatientPortalAccess> => {
  return apiClient.put(`/patients/${patientId}/portal-registrations/${userId}`, { disabled })
}

export const getPortalUserInfo = (): Promise<IPatientPortalUserInfo> => {
  return apiClient.get(`/patients/portal-registrations/profiles`)
}

export const sendConsentToEmail = ({
  patientId,
  contactId,
}: {
  patientId: string | string[] | RouteParams
  contactId: string | string[] | RouteParams
}): Promise<number> => {
  const queryContact = contactId ? `?contactId=${contactId}` : ''
  return apiClient.post(`/patients/${patientId}/consents/emailConsent${queryContact}`)
}

export const toggleContactStatus = ({
  patientId,
  contactId,
  isActive,
}: {
  patientId: string | string[] | RouteParams
  contactId: string | string[] | RouteParams
  isActive: boolean
}): Promise<number> => {
  return apiClient.put(`/patients/${patientId}/contacts/${contactId}/isActive`, { isActive })
}

export const getPatientsSummary = ({ params }: { params: IPatientSummaryParams }): Promise<IPatientsSummary> => {
  return apiClient.get('/patients/summary', { params })
}
